import { useSelector } from 'react-redux';
import xlsx, { IContent } from 'json-as-xlsx';
import { isDirectSalesCampaignCloned } from 'components/pages/Planner/hooks/utils/isDirectSalesCampaignCloned';
import { MetricType } from 'components/common/types/DrilldownReports.types';
import { getDealId } from 'store/dealManagement/selectors/getDealId';
import { AllocationReportData, Frame, ReportType, SelectedLine } from './AllocationReport.types';
import { Store } from '../types/Store.types';
import { generateFileName } from './utils';
import { HOURS, ROW_GROUP_COLUMNS } from './consts';
import { metricTypeToName } from '../DrilldownReports/consts';

interface UseExportAllocationReport {
  exportProgrammaticAllocationReport: () => void;
  exportDirectSalesAllocationReport: () => void;
}

export const useExportAllocationReport = ({
  activeReportType,
  allocationReportData,
  selectedLine,
  selectedMetricType,
  selectedLineCount,
}: {
  activeReportType: ReportType;
  allocationReportData?: AllocationReportData;
  selectedLine: SelectedLine;
  selectedMetricType: MetricType;
  selectedLineCount?: number;
}): UseExportAllocationReport => {
  const campaignId = useSelector(getDealId);
  const { timezone } = useSelector((state: Store) => state.publisher.configuration);

  const isSavedReport = activeReportType === ReportType.SAVED;

  const getFrameGroupByFields = (frame: Frame): Record<string, string | null | undefined> => ({
    channel: frame.channel?.name,
    mediaType: frame.mediaType,
    frameSizeFamily: frame.frameSizeFamily?.name,
    frameSize: frame.frameSize?.name,
    businessArea: frame.businessArea?.name,
    revenueStream: frame.revenueStream?.name,
    productFormat: frame.productFormat?.name,
    country: frame.country?.name,
    county: frame.county?.name,
    district: frame.district?.name,
    city: frame.town?.name,
    tvArea: frame.tvArea?.name,
    conurbation: frame.conurbation?.name,
  });

  const generateReport = (fileName: string, sheetName: string, withRowGroupColumns: boolean): void => {
    if (!allocationReportData) return;

    const isHourLevelData = !!allocationReportData.frames[0].allocations[0].days[0].hours?.length;

    const columns = [
      {
        label: 'Frame ID',
        value: 'frameId',
      },
      ...(withRowGroupColumns
        ? ROW_GROUP_COLUMNS.map(({ name, code }) => ({
            label: name,
            value: code,
          }))
        : []),
      {
        label: 'Week number (Local Time)',
        value: 'weekNumber',
      },
      {
        label: 'Week period (Local Time)',
        value: 'weekPeriod',
      },
      {
        label: 'Day (Local Time)',
        value: 'date',
      },
      ...(isHourLevelData
        ? [
            {
              label: 'Hour (Local Time)',
              value: 'hour',
            },
          ]
        : []),
      {
        label: 'Datetime (Local Time)',
        value: 'dateTime',
      },
      {
        label: metricTypeToName[selectedMetricType],
        value: selectedMetricType,
      },
    ];

    const hourLevelContent = allocationReportData.frames.flatMap((frame) =>
      frame.allocations.flatMap((allocation) =>
        allocation.days.flatMap((date) =>
          HOURS.map(({ id }) => {
            const formattedAllocationHour = id < 10 ? `0${id}` : id;
            const dateTime = `${date.date} ${formattedAllocationHour}:00:00.000 ${timezone}`;
            const weekPeriod = `${allocation.days[0].date} - ${allocation.days.at(-1)!.date}`;

            return {
              frameId: frame.frameId,
              ...getFrameGroupByFields(frame),
              weekNumber: allocation.allocationWeek,
              weekPeriod,
              date: date.date,
              hour: id,
              dateTime,
              [selectedMetricType]: date.hours?.[id] || '',
            };
          }),
        ),
      ),
    );

    const dayLevelContent = allocationReportData.frames.flatMap((frame) =>
      frame.allocations.flatMap((allocation) =>
        allocation.days.flatMap((date) => {
          const dateTime = `${date.date} 00:00:00.000 ${timezone}`;
          const weekPeriod = `${allocation.days[0].date} - ${allocation.days.at(-1)!.date}`;

          return {
            frameId: frame.frameId,
            ...getFrameGroupByFields(frame),
            weekNumber: allocation.allocationWeek,
            weekPeriod,
            date: date.date,
            dateTime,
            [selectedMetricType]: date.total || '',
          };
        }),
      ),
    );

    xlsx(
      [
        {
          sheet: sheetName,
          columns,
          content: (isHourLevelData ? hourLevelContent : dayLevelContent) as unknown as IContent[],
        },
      ],
      { fileName },
    );
  };

  const exportDirectSalesAllocationReport = (): void => {
    const isCampaignCloned = isDirectSalesCampaignCloned(campaignId);
    const reportType = isCampaignCloned && isSavedReport ? 'Existing Saved Allocation' : 'Allocation';

    const fileName = generateFileName({
      activeReportType: reportType,
      selectedMetricType,
      selectedLine,
      selectedLineCount,
    });

    generateReport(fileName, reportType, false);
  };

  const exportProgrammaticAllocationReport = (): void => {
    const fileName = generateFileName({
      activeReportType: isSavedReport ? 'Alloc' : 'Prop',
      selectedMetricType,
      selectedLine,
      selectedLineCount,
    });
    const sheetName = isSavedReport ? 'Allocation Report' : 'Proposal Report';

    generateReport(fileName, sheetName, true);
  };

  return {
    exportDirectSalesAllocationReport,
    exportProgrammaticAllocationReport,
  };
};
